import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

const StyledBox = styled(Box)({
  "& .MuiPaper-root": {
    maxHeight: "15.625rem",
    maxWidth: "21.875rem",
    wordBreak: "break-word",
    overflowY: "scroll",
    "&::-webkit-scrollbar": {
      width: "11px",
      background: "transparent",
      borderTopRightRadius: "4px",
      borderBottomRightRadius: "4px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: 0,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#bdb6b6",
      borderRadius: "4px",
    },
  },
  "& .MuiAlert-action": {
    padding: "0.25rem 0 0 0.3125rem",
  },
  "& .MuiAlert-colorWarning": {
    backgroundColor: "#cc4c0f",
  },
  "& .MuiTypography-root": {
    fontSize: "0.9rem",
    fontWeight: 500,
  },
});

export default function SnackBar(props) {
  const {
    open = false,
    duration = 10000,
    type = "success",
    msg = "Notification",
    position = { vertical: "bottom", horizontal: "right" },
    closeSnackBar = () => {},
    config = {},
  } = props;
  const alertRef = useRef(null);

  // Focus the alert when the snackbar opens
  useEffect(() => {
    let timeout = null;
    if (open && alertRef.current) {
      alertRef.current.focus();
      timeout = setTimeout(() => closeSnackBar(), duration);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [closeSnackBar, duration, open]);

  const { listItems } = config || {};
  let message = msg;

  if (listItems?.length) {
    message = listItems.map((listItem) => (
      <div key={listItem.title}>
        <Typography>{listItem.title}</Typography>
        {(listItem.items || []).length > 0 && (
          <ul style={{ margin: "0.125rem" }}>
            {listItem.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
      </div>
    ));
  }

  return (
    <StyledBox>
      <Snackbar
        aria-describedby="toast-notification"
        aria-live="polite"
        open={open}
        autoHideDuration={duration}
        onClose={closeSnackBar}
        anchorOrigin={position}
      >
        <Alert
          ref={alertRef}
          id="toast-notification"
          variant="filled"
          onClose={closeSnackBar}
          severity={type}
          tabIndex={0}
          role="alert"
          aria-live="assertive"
        >
          {message}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
}

SnackBar.propTypes = {
  open: PropTypes.bool,
  duration: PropTypes.number,
  type: PropTypes.string,
  msg: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  closeSnackBar: PropTypes.func,
  position: PropTypes.instanceOf(Object),
  config: PropTypes.shape({
    listItems: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        items: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    defaultMsg: PropTypes.string,
  }),
};
SnackBar.defaultProps = {
  open: false,
  duration: 10000,
  type: "success",
  msg: "Notification",
  closeSnackBar: () => {},
  position: {
    vertical: "bottom",
    horizontal: "right",
  },
  config: {},
};
